import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link, type Location } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import { getMeta } from '../storage/safe-store';
import { HbsInput } from '../components/hbs/Input';
import { HbsButton } from '../components/hbs/Button';
import { HbsStrengthMeter } from '../components/hbs/StrengthMeter';
import { HeartLogo } from '../components/hbs/HeartLogo';
import { EyeToggle } from '../components/hbs/EyeToggle';
import { SafePasswordTipsModal } from '../components/SafePasswordTipsModal';
import { passwordStrength, MIN_PASSWORD_LENGTH } from '@heybesafe/shared';
import { shouldOpenInCreateMode, markAccountUsed } from '../lib/firstRun';
import { getCampaignSrc, isValidCampaignSrc } from '../lib/campaignParams';
import { warmupBackend } from '../api/client';

type Mode = 'login' | 'register';

export default function AuthPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, loadingStep } = useSafe();

  // Campaign attribution carried from a landing page's `?src=` tag. Validated to
  // the same charset the server enforces. This is now the only source of signup
  // attribution — the self-declared "how did you hear about us?" dropdown that
  // used to back-fill it on organic signups was removed.
  // URL wins; otherwise fall back to the campaign captured at boot. AuthGuard's
  // redirect used to drop the query string entirely, so the captured value is
  // what makes attribution survive an ad click that lands on a protected route.
  const rawSrc = searchParams.get('src') ?? '';
  const srcParam = isValidCampaignSrc(rawSrc) ? rawSrc : (getCampaignSrc() ?? '');

  // AuthGuard redirects unauthenticated users here with state.from set to the
  // route they were trying to reach. After a successful login send them back
  // there instead of always landing on /safe.
  const from = (location.state as { from?: Location } | null)?.from;

  // Lazy initializer, not a useEffect: localStorage is synchronous and
  // useLocation() is available on first render, so the mode is settled before
  // paint. Deciding in an effect would render "Welcome back" and then swap to
  // "Create your safe" — a visible flash on the app's first impression.
  const [mode, setMode] = useState<Mode>(() =>
    shouldOpenInCreateMode(from?.pathname) ? 'register' : 'login',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const [tipsDismissed, setTipsDismissed] = useState(false);

  useEffect(() => {
    // Prefill the email for a returning user from the local vault metadata.
    const vaultEmail = getMeta('email');
    if (vaultEmail) {
      setEmail(vaultEmail);
    }
  }, []);

  // Pre-warm the auth + hydration Lambdas the moment the auth screen loads, so
  // the register/login submit (seconds later, after the user types) hits warm
  // containers instead of cold-starting ~5 Lambdas serially. Fire-and-forget.
  useEffect(() => {
    warmupBackend();
  }, []);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError('');

    if (mode === 'register' && password.length < MIN_PASSWORD_LENGTH) {
      setError('Safe password must be at least 12 characters');
      return;
    }
    if (mode === 'register' && password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // A campaign tag if we have one; otherwise no attribution.
    const signupSource = srcParam || undefined;

    setLoading(true);
    try {
      const result = mode === 'login' ? await login(email, password) : await register(email, password, signupSource);
      if (result?.ok) {
        // Both branches count: someone who registers, logs out and returns is
        // unambiguously an existing user and must not be asked to create again.
        markAccountUsed();
        navigate(from ?? '/safe', { replace: true });
      } else if (result) {
        setError(result.error ?? 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full max-w-sm bg-hbs-surface rounded-hbs-xl border border-hbs-border p-8 shadow-hbs-lg">
      {/* Brand */}
      <div className="flex flex-col items-center gap-3 mb-6">
        <HeartLogo size={56} />
        <span className="font-display font-semibold text-[18px] tracking-hbs-tight text-hbs-text">
          Hey Be Safe
        </span>
      </div>

      <h1 className="font-display text-[22px] tracking-hbs-tight text-hbs-text mb-1 text-center">
        {mode === 'login' ? 'Welcome back' : 'Create your safe'}
      </h1>
      <p className="text-[13px] text-hbs-text-mute text-center mb-6">
        {mode === 'login'
          ? 'Enter your credentials to unlock your safe'
          : 'Pick an email and a strong safe password'}
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <HbsInput
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          placeholder="you@example.com"
          autoFocus
          required
          name="email"
        />

        <div>
          <HbsInput
            label="Safe password"
            type={revealed ? 'text' : 'password'}
            value={password}
            onChange={setPassword}
            required
            mono={revealed}
            name="password"
            onFocus={() => {
              if (mode === 'register' && !tipsDismissed) {
                setShowTips(true);
              }
            }}
            trailingActions={<EyeToggle revealed={revealed} onChange={setRevealed} />}
          />
          {mode === 'register' && password.length > 0 && (
            <div className="mt-2">
              <HbsStrengthMeter strength={passwordStrength(password)} />
            </div>
          )}
        </div>

        {mode === 'register' && (
          <HbsInput
            label="Confirm safe password"
            type="password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            required
            name="confirm-password"
            error={
              confirmPassword.length > 0 && password !== confirmPassword
                ? 'Passwords do not match'
                : undefined
            }
          />
        )}

        {error && (
          <p className="text-[13px] text-hbs-danger bg-hbs-danger/10 rounded-hbs-sm p-3" role="alert">
            {error}
          </p>
        )}

        {mode === 'register' && (
          <p className="text-[12px] text-hbs-text-mute bg-hbs-surface-alt rounded-hbs-sm p-3 leading-relaxed">
            Your safe password is never sent to our servers. It locally encrypts your safe using AES-256.
          </p>
        )}

        <div className="flex flex-col gap-2 mt-2">
          <HbsButton id="auth-submit-button" type="submit" loading={loading} fullWidth>
            {loading && loadingStep ? loadingStep : mode === 'login' ? 'Unlock Safe' : 'Create Account'}
          </HbsButton>
          <HbsButton
            id="auth-toggle-mode-button"
            type="button"
            variant="plain"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setError('');
              setTipsDismissed(false);
            }}
          >
            {mode === 'login' ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
          </HbsButton>
        </div>
        {mode === 'login' && (
          <div className="text-center mt-4">
            <Link
              to="/auth/forgot-password"
              className="text-sm text-hbs-text-mute hover:text-hbs-text"
            >
              Forgot password?
            </Link>
          </div>
        )}
      </form>

      <p className="text-[11px] text-hbs-text-mute text-right mt-6" aria-label="Build identifier">
        v{__APP_VERSION__} ({__GIT_SHA__})
      </p>

      <SafePasswordTipsModal
        open={showTips}
        onClose={() => {
          setShowTips(false);
          setTipsDismissed(true);
        }}
      />
    </div>
  );
}
